'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { RefObject, FC } from 'react';
import dynamic from 'next/dynamic';

import { DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeader';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useUrl } from '@packages/shared/src/hooks/useUrl/useUrl';

import { ResponsiveNavigationMainItem } from './ResponsiveNavigationMainItem';
import type { NavigationItem, RawNavigationItem } from './navigationTypes';
import { useCleanedNavData } from './useCleanedNavData';
import type { UseTreeNavigationPopupOptions } from './Flyout/useTreeNavigationPopup';
import { useTreeNavigationPopup } from './Flyout/useTreeNavigationPopup';
import { useNavigationTracking } from './useNavigationTracking';
import { HamburgerButton } from './Internals/HamburgerButton';
import { useToggleTransition } from './useToggleTransition';
import { SecondaryNavigationContainer } from './Internals/SecondaryNavigationContainer';
import { PrimaryNavigationContainer } from './Internals/PrimaryNavigationContainer';
import { ResponsiveNavigationLayout } from './Internals/ResponsiveNavigationLayout';
import { MetaNavigationLinks } from '../../MetaNavigation/MobileLinks/MetaNavigationLinks.client';

const ResponsiveFlyout = dynamic(() => import('./Flyout/ResponsiveFlyout'), {
  ssr: false,
});

export type ResponsiveNavigationProps = {
  /** The nav tree, as a polyforest (in the graph theory sense) of navigation items */
  items: RawNavigationItem[];
} & Pick<UseTreeNavigationPopupOptions, 'mode' | 'onFirstInteraction'>;

/**
 * Navigation component, which displays a horizontal navigation bar on desktop and a hamburger menu on mobile. Shows {@link ResponsiveFlyout} on user interaction, to enable drilling into the nav tree.
 */
export const ResponsiveNavigation: FC<ResponsiveNavigationProps> = ({
  items,
  mode = 'click',
  onFirstInteraction,
}) => {
  const {
    shouldBeMounted: hamburgerShouldBeMounted,
    classNames: hamburgerClassNames,
    toggle: toggleHamburgerMenu,
    hide: hideHamburgerMenu,
    state,
  } = useToggleTransition(300);

  const { isDesktop } = useDeviceType();

  const url = useUrl();
  const {
    navigationDirection,
    showPopup,
    isSelected,
    isRoot,
    selectMainItem,
    selectPopupItem,
    backToParent,
    closePopup,
  } = useTreeNavigationPopup({ onFirstInteraction });

  useEffect(() => {
    // INSPECT-978 prevent body scroll when the menu is open
    // this is only needed for the logged out myaccount navigation because the content is too short and
    // overscroll-contain is only working for content that is scrollable
    // the logged in myaccount navigation is scrollable and so body locking is not needed
    if (!isDesktop && state.startsWith('enter')) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [state, isDesktop]);

  const closeHamburgerMenu = useCallback(() => {
    closePopup();
    hideHamburgerMenu();
  }, [closePopup, hideHamburgerMenu]);

  const allowHoverInteractions = isDesktop && mode === 'hover';

  const closeOnMouseLeaveUnlessMovingInto = useCallback(
    (allowedTarget: RefObject<HTMLDivElement | HTMLUListElement | null>) =>
      (event: React.MouseEvent<HTMLDivElement | HTMLUListElement>) => {
        if (!allowHoverInteractions) return;

        const mouseIsEnteringAllowedElement =
          event.relatedTarget instanceof Node &&
          allowedTarget.current?.contains(event.relatedTarget);

        if (!mouseIsEnteringAllowedElement) {
          closePopup();
        }
      },
    [allowHoverInteractions, closePopup],
  );

  const primaryNavigationRef = useRef<HTMLUListElement | null>(null);
  const secondaryNavigationRef = useRef<HTMLDivElement | null>(null);

  const handlePrimaryMouseLeave = useMemo(
    () => closeOnMouseLeaveUnlessMovingInto(secondaryNavigationRef),
    [closeOnMouseLeaveUnlessMovingInto, secondaryNavigationRef],
  );

  const handleSecondaryMouseLeave = useMemo(
    () => closeOnMouseLeaveUnlessMovingInto(primaryNavigationRef),
    [closeOnMouseLeaveUnlessMovingInto, primaryNavigationRef],
  );

  useEffect(() => {
    closeHamburgerMenu();
  }, [url, closeHamburgerMenu]);

  const { trackNavigationTo } = useNavigationTracking();

  const handleNavigate = (item: NavigationItem) => {
    trackNavigationTo(item);
    closeHamburgerMenu();
  };

  const selectMainItemWithTracking: typeof selectMainItem = (...args) => {
    selectMainItem(...args);
  };

  const { cleanItems, flatItems } = useCleanedNavData(items);

  const selectedItem = flatItems.find(isSelected);

  return (
    <ResponsiveNavigationLayout
      hamburgerClassNames={hamburgerClassNames}
      hamburgerShouldBeMounted={hamburgerShouldBeMounted}
      button={<HamburgerButton onClick={toggleHamburgerMenu} />}
      isLevel2Visible={showPopup}
      onClickAway={closePopup}
    >
      <div className="[grid-area:header] lg:hidden">
        <DialogHeader
          onBack={selectedItem ? backToParent : closeHamburgerMenu}
          onClose={closeHamburgerMenu}
        >
          {selectedItem?.displayName ?? ''}
        </DialogHeader>
      </div>

      <div className="overflow-hidden [grid-area:content] max-lg:overflow-y-auto max-lg:overscroll-contain">
        {!showPopup && <MetaNavigationLinks />}

        <PrimaryNavigationContainer
          ref={primaryNavigationRef}
          primaryNavigationVisible={!selectedItem}
          onMouseLeave={handlePrimaryMouseLeave}
        >
          {cleanItems.map((item) => (
            <ResponsiveNavigationMainItem
              key={item.uri}
              href={item.uri}
              imageUrl={item.image}
              allowNavigation={allowHoverInteractions || item.children.length === 0}
              isOpen={showPopup && isRoot(item)}
              // trailing slash is stripped for the comparison, because `url` has no trailing slash by default
              isActive={item.uri !== undefined && url.startsWith(item.uri.replace(/\/$/, ''))}
              onHover={() => {
                if (allowHoverInteractions) {
                  selectMainItemWithTracking(item, true);
                }
              }}
              onClick={() => {
                selectMainItemWithTracking(item);
              }}
              onNavigate={() => handleNavigate(item)}
            >
              {item.displayName}
            </ResponsiveNavigationMainItem>
          ))}
        </PrimaryNavigationContainer>
      </div>

      <SecondaryNavigationContainer
        ref={secondaryNavigationRef}
        isVisible={showPopup}
        onMouseLeave={handleSecondaryMouseLeave}
      >
        <ResponsiveFlyout
          isVisible={showPopup}
          item={selectedItem}
          direction={navigationDirection}
          onBack={!selectedItem || isRoot(selectedItem) ? undefined : backToParent}
          onClose={closePopup}
          onItemClick={selectPopupItem}
          onNavigate={handleNavigate}
        />
      </SecondaryNavigationContainer>
    </ResponsiveNavigationLayout>
  );
};
